
import React from 'react';
import { CalculatorCategory } from '../lib/calculatorUtils';
import { Calculator, DollarSign, Heart, Briefcase, BookOpen, Smile } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface CategoryCardProps {
  category: CalculatorCategory;
}

/** Theme-specific icon mapping */
const themeIcons: Record<string, React.ReactNode> = {
  finance: <DollarSign className="h-5 w-5" />,
  health: <Heart className="h-5 w-5" />,
  education: <BookOpen className="h-5 w-5" />,
  lifestyle: <Smile className="h-5 w-5" />,
  professional: <Briefcase className="h-5 w-5" />,
  everyday: <Calculator className="h-5 w-5" />,
};

/** Pre-defined theme class maps — avoids Tailwind purge issues with dynamic class names */
const themeStyles: Record<string, { badge: string; title: string; pillBg: string; pillText: string; pillHover: string; link: string }> = {
  finance: {
    badge: 'bg-finance text-finance-foreground',
    title: 'text-finance',
    pillBg: 'rgba(99, 102, 241, 0.08)',
    pillText: 'text-finance-accent',
    pillHover: 'rgba(99, 102, 241, 0.16)',
    link: 'text-finance',
  },
  health: {
    badge: 'bg-health text-health-foreground',
    title: 'text-health',
    pillBg: 'rgba(16, 185, 129, 0.08)',
    pillText: 'text-health-accent',
    pillHover: 'rgba(16, 185, 129, 0.16)',
    link: 'text-health',
  },
  education: {
    badge: 'bg-education text-education-foreground',
    title: 'text-education',
    pillBg: 'rgba(139, 92, 246, 0.08)',
    pillText: 'text-education-accent',
    pillHover: 'rgba(139, 92, 246, 0.16)',
    link: 'text-education',
  },
  lifestyle: {
    badge: 'bg-lifestyle text-lifestyle-foreground',
    title: 'text-lifestyle',
    pillBg: 'rgba(236, 72, 153, 0.08)',
    pillText: 'text-lifestyle-accent',
    pillHover: 'rgba(236, 72, 153, 0.16)',
    link: 'text-lifestyle',
  },
  professional: {
    badge: 'bg-professional text-professional-foreground',
    title: 'text-professional',
    pillBg: 'rgba(100, 116, 139, 0.08)',
    pillText: 'text-professional-accent',
    pillHover: 'rgba(100, 116, 139, 0.16)',
    link: 'text-professional',
  },
  everyday: {
    badge: 'bg-everyday text-everyday-foreground',
    title: 'text-everyday',
    pillBg: 'rgba(245, 158, 11, 0.08)',
    pillText: 'text-everyday-accent',
    pillHover: 'rgba(245, 158, 11, 0.16)',
    link: 'text-everyday',
  },
};

const CategoryCard: React.FC<CategoryCardProps> = ({ category }) => {
  const icon = themeIcons[category.theme] || <Calculator className="h-5 w-5" />;
  const styles = themeStyles[category.theme] || themeStyles.everyday;
  const categoryPath = category.id;

  return (
    <div className="group rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      {/* Category header */}
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("flex items-center justify-center w-10 h-10 rounded-xl shadow-sm", styles.badge)}>
          {icon}
        </div>
        <h3 className={cn("text-lg font-bold", styles.title)}>
          {category.name}
        </h3>
      </div>

      <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
        {category.description}
      </p>

      {/* Calculator links */}
      <div className="flex flex-wrap gap-2">
        {category.calculators.map((calc) => (
          <Link
            key={calc.id}
            to={`/calculators/${categoryPath}/${calc.id}`}
            className={cn(
              "text-sm px-3 py-1.5 rounded-lg font-medium transition-all duration-200 min-h-[34px] flex items-center",
              styles.pillText
            )}
            style={{ backgroundColor: styles.pillBg }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.pillHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = styles.pillBg)}
          >
            {calc.name}
          </Link>
        ))}
      </div>

      {/* View all link */}
      <Link
        to={`/calculators/${categoryPath}`}
        className={cn("inline-flex items-center gap-1 mt-5 text-sm font-semibold hover:underline", styles.link)}
      >
        View all {category.calculators.length} calculators →
      </Link>
    </div>
  );
};

export default CategoryCard;
